// The zone tool (issue 1149; design/30 §1.3): the DM's mode in which a drag on the board paints a box of squares into a
// vision zone — a fog bank, magical darkness, squares never revealed, or difficult terrain (issue 1151) — and a click paints one; the Erase mode takes
// squares out of every zone. Like the fog tool it is held outside React, fed presses and releases by the canvas, and
// each release is ONE event, sent by whoever listens (the board's `ZoneGate`). Painting the same kind again adds to the
// zone last painted of that kind, so a zone drawn in several strokes is one zone. One board mode at a time: starting
// this stops every other tool, and each of those stops this.
import { type Cell, type FogRect, type ZoneBody, type ZoneKind } from '@dragons-table/vtt-shared';
import { fogRect, fogTool } from './fogTool';
import { wallTool } from './wallTool';
import { focusTool } from './focusTool';
import { measureTool } from './measureTool';
import { targetTool } from './targetTool';
import { areaTool } from './areaTool';
import { drawTool } from './drawTool';
import { whatsThisTool } from './whatsThisTool';

export type ZoneMode = ZoneKind | 'erase';
/** Each kind's tint on the DM's board (and its swatch in the bar). */
export const ZONE_TINTS: Readonly<Record<ZoneKind, number>> = { fog_bank: 0x94a3b8, darkness: 0x6d28d9, never_revealed: 0xdc2626, difficult: 0xb45309, sound: 0x0d9488 };
export interface ZoneDraft {
  on: boolean;
  mode: ZoneMode;
  /** The box being dragged; null between drags. */
  box: { from: Cell; to: Cell } | null;
}

/** What one release sends: the box painted into the zone `id` of the mode's kind, or erased from every zone. */
export function zoneCommit(mode: ZoneMode, from: Cell, to: Cell, id: string): ZoneBody {
  const rect: FogRect = fogRect(from, to);
  return mode === 'erase' ? { kind: 'zone.erase', rect } : { kind: 'zone.paint', id, zone: mode, rect };
}

type Listener = (d: ZoneDraft) => void;
let draft: ZoneDraft = { on: false, mode: 'darkness', box: null };
/** The zone last painted of each kind in this run of the tool: another stroke adds to it. */
let painting: Partial<Record<ZoneKind, string>> = {};
/** Those the DM's store has been seen to hold: one gone from it since (erased away, removed) is not added to again. */
let landed = new Set<string>();
const listeners = new Set<Listener>();
const committers = new Set<(c: ZoneBody) => void>();
const set = (next: ZoneDraft) => { draft = next; for (const l of listeners) l(draft); };

export const zoneTool = {
  get: () => draft,
  subscribe(listener: Listener): () => void { listeners.add(listener); return () => { listeners.delete(listener); }; },
  onCommit(fn: (c: ZoneBody) => void): () => void { committers.add(fn); return () => { committers.delete(fn); }; },
  start(mode: ZoneMode = draft.mode): void { whatsThisTool.stop();
    focusTool.stop(); measureTool.stop(); areaTool.stop(); targetTool.stop(); drawTool.stop(); wallTool.stop(); fogTool.stop();
    painting = {}; landed = new Set();
    set({ on: true, mode, box: null });
  },
  stop(): void { if (draft.on || draft.box) set({ ...draft, on: false, box: null }); },
  toggle(): void { if (draft.on) zoneTool.stop(); else zoneTool.start(); },
  mode(mode: ZoneMode): void { if (draft.on && draft.mode !== mode) set({ ...draft, mode, box: null }); },
  press(c: Cell): void { if (draft.on) set({ ...draft, box: { from: c, to: c } }); },
  drag(c: Cell): void { if (draft.on && draft.box && (draft.box.to.i !== c.i || draft.box.to.j !== c.j)) set({ ...draft, box: { from: draft.box.from, to: c } }); },
  /** A press the browser took back (`pointercancel`): the box goes, nothing is sent, the mode stays on. */
  cancel(): void { if (draft.box) set({ ...draft, box: null }); },
  /**
   * The release: one event for the whole box, sent once; the mode stays on for the next. `known` are the zones the
   * DM's store holds: a stroke adds to the last zone of its kind only while that zone is still there.
   */
  release(c: Cell, known: ReadonlyArray<{ id: string }> = [], newId: () => string = () => crypto.randomUUID()): void {
    if (!draft.on || !draft.box) return;
    let id = '';
    if (draft.mode !== 'erase') {
      const had = painting[draft.mode];
      const there = !!had && known.some((z) => z.id === had);
      if (there) landed.add(had);
      id = had && (there || !landed.has(had)) ? had : newId();
      painting[draft.mode] = id;
    }
    const commit = zoneCommit(draft.mode, draft.box.from, c, id);
    set({ ...draft, box: null });
    for (const fn of committers) fn(commit);
  },
  /** A new zone of the current kind with the next stroke (the DM's "New zone"). */
  fresh(): void { if (draft.mode !== 'erase') delete painting[draft.mode]; },
};
